import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import to from 'await-to-js';
import axios from 'axios';
import { each, get, omit } from 'lodash';
import i18n from '@/plugins/vue-i18n';
import { jobApi } from '@/helpers';
import handleErrorMessages from './utils/validation';

export const useSwitchingMatricesStore = defineStore('switchingMatrices', () => {
  const { t } = i18n;

  const scenariosStore = useScenariosStore();
  const snackbarStore = useSnackbarStore();
  // State
  const switchingStatus = shallowRef('Stopped');
  const switchingMatrixForCannGroup = ref(null);
  const scenarioSwitchingMatrices = ref([]);
  const selectedSwitchingMatrix = ref(null);
  const switchingMatricesUnclusteredCount = shallowRef(0);
  const loading = shallowRef(false);

  // Actions
  async function fetchCannGroupSwitchingMatrix({ params = {} } = {}) {
    loading.value = true;
    const [err, response] = await to(
      axios.get(`/api/switching-matrices/${params.scenarioId}/${params.cannGroupId}`, {
        params: omit(params, ['scenarioId', 'cannGroupId']),
      })
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    switchingMatrixForCannGroup.value = response.data;
    loading.value = false;
    return response.data;
  }

  async function fetchScenarioSwitchingMatrices({ where = {} } = {}) {
    loading.value = true;
    const [err, response] = await to(axios.get('/api/switching-matrices', { params: { where } }));
    loading.value = false;
    if (err) throw new Error(err.message);
    scenarioSwitchingMatrices.value = response.data;
    loading.value = false;
    return response.data;
  }

  async function fetchSwitchingMatricesCount() {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(axios.get(`/api/switching-matrices/${scenarioId}`));
    loading.value = false;
    if (err) throw new Error(err.message);
    switchingMatricesUnclusteredCount.value = response.data.data;
    loading.value = false;
  }

  function setSelectedScenario(newSelectedSwitchingMatrix) {
    selectedSwitchingMatrix.value = newSelectedSwitchingMatrix;
  }

  async function runSwitchingJob({ params = {}, commit } = {}) {
    loading.value = true;
    const [err, response] = await to(
      axios.post(`/api/scenarios/${params.scenarioId}/run-switching`, { commit })
    );
    loading.value = false;
    if (err) throw new Error(err);
    handleErrorMessages({ response });
    return response;
  }

  async function exportSwitching(params) {
    const scenarioId = scenariosStore.selectedScenario._id;
    const config = { params, responseType: 'arraybuffer' };
    const [err, res] = await to(axios.get(`/api/scenarios/${scenarioId}/switching-export`, config));
    if (err) throw new Error(err.message);
    return {
      filename: res.headers['content-disposition'].split('filename=')[1],
      buffer: res.data,
    };
  }

  async function exportSwitchingCannGroup({ params, cannGroupId }) {
    const scenarioId = scenariosStore.selectedScenario._id;
    const config = { params, responseType: 'arraybuffer' };
    const [err, res] = await to(
      axios.get(`/api/scenarios/${scenarioId}/switching-export/${cannGroupId}`, config)
    );
    if (err) throw new Error(err.message);
    return {
      filename: res.headers['content-disposition'].split('filename=')[1],
      buffer: res.data,
    };
  }

  async function updateSwitchingMatrixMetrics(params) {
    loading.value = true;
    try {
      await to(
        axios.patch('/api/switching-matrices', {
          updates: [
            {
              filter: {
                scenarioId: params.scenarioId,
                cannGroupId: params.cannGroupId,
              },
              operator: '$set',
              fields: params.fields,
            },
          ],
        })
      );
      loading.value = false;
    } catch (err) {
      loading.value = false;
      throw new Error(err.message);
    }
  }

  async function runSimulationWithBias(params) {
    try {
      const scenarioId = params.payload.scenario_id;
      const cannGroupId = params.payload.calculations[0].cannGroupId; // every item in calculations list will have the same cann group id
      const similarityBias = params.payload.calculations[0].similarityBias;
      await jobApi.runFunction('run-switching-modelling', params.payload);

      // Save new simulation bias value to metrics (updating for all clusters at once)
      await updateSwitchingMatrixMetrics({
        scenarioId,
        cannGroupId,
        fields: { similarityBias },
      });

      each(params.payload.calculations, ({ clusterId }) => {
        fetchCannGroupSwitchingMatrix({
          params: {
            scenarioId,
            cannGroupId,
            clusterId,
          },
        });
      });

      snackbarStore.showSuccess(t('messages.switchingSimulationSuccess'));
    } catch (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const failedMessage = 'messages.switchingSimulationFailed';
      const message = t(key, get(err, 'response.data.messageParams', failedMessage));

      snackbarStore.showError(t(failedMessage));
      throw new Error(message);
    }
  }

  async function runSimulationSetToUnclustered(params) {
    try {
      const scenarioId = params.payload.scenario_id;
      await jobApi.runFunction('run-switching-modelling', params.payload);

      each(params.payload.calculations, ({ clusterId, cannGroupId }) => {
        fetchCannGroupSwitchingMatrix({
          params: {
            scenarioId,
            cannGroupId,
            clusterId,
          },
        });
      });

      snackbarStore.showSuccess(t('messages.switchingSimulationSuccess'));
    } catch (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const failedMessage = 'messages.switchingSimulationFailed';
      const message = t(key, get(err, 'response.data.messageParams', failedMessage));

      snackbarStore.showError(t(failedMessage));
      throw new Error(message);
    }
  }

  async function undoSwitchingModellingDecisions(payload) {
    loading.value = true;
    const [err, response] = await to(
      axios.patch('/api/switching-matrices/switching-modelling/undo', payload)
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    snackbarStore.showSuccess(t('messages.switchingSimulationSuccess'));
    return response.data;
  }

  return {
    // State
    switchingStatus,
    switchingMatrixForCannGroup,
    scenarioSwitchingMatrices,
    selectedSwitchingMatrix,
    switchingMatricesUnclusteredCount,
    loading,
    // Actions
    fetchCannGroupSwitchingMatrix,
    fetchScenarioSwitchingMatrices,
    fetchSwitchingMatricesCount,
    setSelectedScenario,
    runSwitchingJob,
    exportSwitching,
    exportSwitchingCannGroup,
    updateSwitchingMatrixMetrics,
    runSimulationWithBias,
    runSimulationSetToUnclustered,
    undoSwitchingModellingDecisions,
  };
});
